import { DIGITAL_SIGNATURE_MIN_AGE } from "../../../config";
import { toJSTDate } from "../../../libs/date";
import { t } from "../../../libs/i18n";
import {
  type DigitalSignatureCertificateStatus,
  getDigitalSignatureCertificateStatus,
  openDigitalSignatureReregistration,
} from "../../../libs/sdk";

// 生年月日が取れない人は15歳以上として扱い、署名用での登録を求める。
// 付与時のバックエンド判定と同じく、JST の暦日で満年齢を数える
export function requiresDigitalSignatureAt(
  birthday: Date | null,
  now: Date,
): boolean {
  if (!birthday) {
    return true;
  }
  const b = toJSTDate(birthday);
  const n = toJSTDate(now);
  let age = n.getFullYear() - b.getFullYear();
  if (
    n.getMonth() < b.getMonth() ||
    (n.getMonth() === b.getMonth() && n.getDate() < b.getDate())
  ) {
    age--;
  }
  return age >= DIGITAL_SIGNATURE_MIN_AGE;
}

export type DigitalSignatureGateResult =
  | { result: "ready" }
  | { result: "registration-finished" }
  | { result: "cancelled" }
  | { result: "error"; message: string };

const statusError = (
  status: Exclude<DigitalSignatureCertificateStatus, "valid" | "invalid">,
): DigitalSignatureGateResult => ({
  result: "error",
  message:
    status === "unavailable"
      ? t(
          "署名用電子証明書の登録状況を確認できませんでした。時間をおいて再度お試しください",
        )
      : t(
          "署名用電子証明書の登録状況を確認できませんでした。解決しない場合は、設定の「ミニアプリ情報（お問い合わせ先など）」からお問い合わせください",
        ),
});

// 「準備する」を押した時点で判定する。ホームを開いたあとに登録し直した人を取りこぼさないため
export async function ensureDigitalSignatureCertificate(): Promise<DigitalSignatureGateResult> {
  const status = await getDigitalSignatureCertificateStatus();
  if (status === "valid") {
    return { result: "ready" };
  }
  // 登録済みの人に不要なカード読み取りをさせないよう、確認できないときは登録画面へ送らない
  if (status !== "invalid") {
    return statusError(status);
  }

  const registration = await openDigitalSignatureReregistration();
  if (registration === "success") {
    return { result: "registration-finished" };
  }
  // 利用者が自分でやめたのでトーストは出さない
  if (registration === "cancel") {
    return { result: "cancelled" };
  }
  return {
    result: "error",
    message: t("署名用電子証明書を登録できませんでした"),
  };
}
